import React, { ReactElement, useState } from 'react'
import styled from 'styled-components'
import DateSelector2 from './DateSelector2'
import CountrySelector from './CountrySelector'
import { nYearsAgo, simpleEmailValidation, splitDateTime } from '../util/Util'
import { RADOM_COLORS } from '../util/Constants'
import { EU_TRAVEL_RULES_COMPLIANCE_TEXT, RADOM_PRIVACY_CONFIRMATION } from '../util/BoilerPlate'
import { PrimaryButton } from './Button'
import { InputLabel, TextInput } from './Input'
import { generateRoundFlagIconFromCountryCode } from '../util/Flags'
import Checkbox from './Checkbox'
import Switch from './Switch'

const TravelRuleFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: auto;
  max-width: 420px;
  font-size: 14px;
`

const TravelRuleFieldsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`
// ---------------------------------------------------------------------------------------------------------------------
// Define mandatory fields needed for EU travel rule compliance
export interface TravelRuleFields {
  countryOfResidence?: string
  dateOfBirth?: string
  emailAddress?: string
  name?: string
  companyName?: string
  accountType?: string
}

export function emptyTravelRuleFields(): TravelRuleFields {
  return {
    countryOfResidence: '',
    dateOfBirth: '',
    emailAddress: '',
    name: '',
    companyName: '',
    accountType: ''
  }
}

// Travel rule field collection flags
export interface TravelRuleFieldsCollected {
  countryOfResidenceCollected: boolean
  dateOfBirthCollected: boolean
  emailAddressCollected: boolean
  nameCollected: boolean
  companyNameCollected: boolean
}

export function zeroTravelRuleFieldsCollected(): TravelRuleFieldsCollected {
  return {
    countryOfResidenceCollected: false,
    dateOfBirthCollected: false,
    emailAddressCollected: false,
    nameCollected: false,
    companyNameCollected: false
  }
}

export function allTravelRuleFieldsCollected(): TravelRuleFieldsCollected {
  return {
    countryOfResidenceCollected: true,
    dateOfBirthCollected: true,
    emailAddressCollected: true,
    nameCollected: true,
    companyNameCollected: true
  }
}

export enum CustomerType {
  Individual = 'Individual',
  Business = 'Business'
}

// ---------------------------------------------------------------------------------------------------------------------
// To avoid accepting implausible dates of birth, the customer is required to enter
// a date that puts their age between 16 and 110 years old
// The choice of these ages is entirely arbitrary!
const MIN_CUSTOMER_AGE_YEARS: number = 16
const MAX_CUSTOMER_AGE_YEARS: number = 110
export const youngestPermittedDateOfBirth: Date = nYearsAgo(MIN_CUSTOMER_AGE_YEARS)
export const oldestPermittedDateOfBirth: Date = nYearsAgo(MAX_CUSTOMER_AGE_YEARS)

interface IProps {
  travelRuleFieldsCollected: TravelRuleFieldsCollected
  onTravelRuleFieldsComplete: (trFields: TravelRuleFields) => void
}

// Alternate label text for individuals and businesses
const labelText: Record<string, [string, string]> = {
  name: ['Name', 'Representative Name'],
  email: ['Email Address', 'Email Address'],
  dob: ['Date of Birth', 'Representative Date of Birth'],
  country: ['Country', 'Company Registration Country'],
  company: ['', 'Company Name']
}

function generateInputLabel(labelName: string, isBusiness: boolean): ReactElement {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '15px' }}>
    <InputLabel>{labelText[labelName][+isBusiness]}</InputLabel>
  </div>
}

// ---------------------------------------------------------------------------------------------------------------------
const TravelRuleFieldsWidget = (props: IProps): React.ReactElement => {
  // Start by assuming that the travel rules are being collected for an individual not a business
  const [isBusiness, setIsBusiness] = useState(false)
  const toggleIsBusiness = (): void => {
    const state = isBusiness
    setIsBusiness(!state)
  }

  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const togglePrivacy = (e: React.MouseEvent): void => {
    e.stopPropagation()
    setPrivacyAccepted(previous => !previous)
  }

  const [collectedTravelRuleFields, setCollectedTravelRuleFields] = useState(emptyTravelRuleFields())
  const [errorMessage, setErrorMessage] = useState<string>()

  return <TravelRuleFieldsContainer>
    {
      // ---------------------------------------------------------------------------------------------------------------
      // Error toast
      errorMessage && errorMessage.length > 0 &&
      <div style={{
        backgroundColor: RADOM_COLORS.ERROR,
        padding: 15,
        marginBottom: 10,
        borderRadius: 5,
        fontSize: 14,
        color: 'white',
        overflowWrap: 'break-word'
      }}>
        {errorMessage}
      </div>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // EU Compliance banner
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {EU_TRAVEL_RULES_COMPLIANCE_TEXT}
        <div>
          {generateRoundFlagIconFromCountryCode('EU', 36)}
        </div>
      </div>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Individual/Business switch
      <div style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', marginTop: 20 }} onClick={toggleIsBusiness}>
        <Switch
          checked={isBusiness}
          onClick={toggleIsBusiness}
        />
        <div style={{
          flex: 1,
          color: RADOM_COLORS.BLACK,
          fontSize: 14,
          overflowWrap: 'break-word',
          marginLeft: 10
        }}>
          Paying as a business?
        </div>
      </div>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Name
      !props.travelRuleFieldsCollected.nameCollected &&
      <TravelRuleFieldsRow key="missingName">
        {generateInputLabel('name', isBusiness)}
        {
          <TextInput
            key='trName'
            value={collectedTravelRuleFields.name}
            placeholder={'Enter name'}
            required={true}
            onInput={el => {
              const newTrFields = { ...collectedTravelRuleFields }
              newTrFields.name = (el.target as HTMLInputElement).value
              setCollectedTravelRuleFields(newTrFields)
            }}
          />
        }
      </TravelRuleFieldsRow>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Email address
      !props.travelRuleFieldsCollected.emailAddressCollected &&
      <TravelRuleFieldsRow key="missingEmailAddress">
        {generateInputLabel('email', isBusiness)}
        {
          <TextInput
            key='trEmailAddress'
            value={collectedTravelRuleFields.emailAddress}
            placeholder={'Enter email address'}
            required={true}
            onInput={el => {
              const newTrFields = { ...collectedTravelRuleFields }
              newTrFields.emailAddress = (el.target as HTMLInputElement).value.toLowerCase()
              setCollectedTravelRuleFields(newTrFields)
            }}
          />
        }
      </TravelRuleFieldsRow>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Date of birth
      !props.travelRuleFieldsCollected.dateOfBirthCollected &&
      <TravelRuleFieldsRow key="missingDateOfBirth">
        {generateInputLabel('dob', isBusiness)}
        {
          <DateSelector2
            key='trDateOfBirth'
            startDate={oldestPermittedDateOfBirth}
            endDate={youngestPermittedDateOfBirth}
            value={collectedTravelRuleFields.dateOfBirth}
            onChange={date => {
              if (date) {
                const newTrFields = { ...collectedTravelRuleFields }
                newTrFields.dateOfBirth = splitDateTime(date).datePart
                setCollectedTravelRuleFields(newTrFields)
              }
            }}
            placeholder="Select date of birth"
          />
        }
      </TravelRuleFieldsRow>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Company Name - only required if paying as a business
      isBusiness && !props.travelRuleFieldsCollected.companyNameCollected &&
      <TravelRuleFieldsRow key="missingCompanyName">
        {generateInputLabel('company', true)}
        {
          <TextInput
            key='trCompanyName'
            value={collectedTravelRuleFields.companyName}
            placeholder={'Enter your company name'}
            required={true}
            onInput={el => {
              const newTrFields = { ...collectedTravelRuleFields }
              newTrFields.companyName = (el.target as HTMLInputElement).value
              setCollectedTravelRuleFields(newTrFields)
            }}
          />
        }
      </TravelRuleFieldsRow>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Country of residence
      !props.travelRuleFieldsCollected.countryOfResidenceCollected &&
      <TravelRuleFieldsRow key="missingCountryOfResidence">
        {generateInputLabel('country', isBusiness)}
        {
          <CountrySelector
            selectedCountry={collectedTravelRuleFields.countryOfResidence}
            onCountrySelected={country => {
              if (country) {
                const newTrFields = { ...collectedTravelRuleFields }
                newTrFields.countryOfResidence = country.name
                setCollectedTravelRuleFields(newTrFields)
              }
            }}
          />
        }
      </TravelRuleFieldsRow>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // EU Privacy confirmation
      <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginTop: 20 }} onClick={togglePrivacy}>
        <Checkbox
          checked={privacyAccepted}
          onClick={togglePrivacy}
        />
        {RADOM_PRIVACY_CONFIRMATION}
      </div>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Blue "Continue" button
      <PrimaryButton
        disabled={!privacyAccepted}
        style={{ background: RADOM_COLORS.BLUE4, width: '100%', marginTop: '25px' }}
        onClick={() => {
          const errorMsgs: string[] = []

          collectedTravelRuleFields.accountType = isBusiness ? CustomerType.Business : CustomerType.Individual

          if (!props.travelRuleFieldsCollected.countryOfResidenceCollected &&
            !collectedTravelRuleFields.countryOfResidence) {
            errorMsgs.push('Country of residence')
          }
          if (!props.travelRuleFieldsCollected.dateOfBirthCollected && !collectedTravelRuleFields.dateOfBirth) {
            errorMsgs.push('Date of birth')
          }
          if (!props.travelRuleFieldsCollected.emailAddressCollected &&
            (!collectedTravelRuleFields.emailAddress ||
              !simpleEmailValidation(collectedTravelRuleFields.emailAddress))) {
            errorMsgs.push('Email address')
          }
          if (!props.travelRuleFieldsCollected.nameCollected && !collectedTravelRuleFields.name) {
            errorMsgs.push('Name')
          }

          // Are any of the required fields still missing?
          if (errorMsgs.length > 0) {
            setErrorMessage(
              `Please enter ${errorMsgs.length === 1 ? 'a valid value' : 'valid values'} for ${errorMsgs.join(', ')}`
            )
          } else {
            props.onTravelRuleFieldsComplete(collectedTravelRuleFields)
            setErrorMessage('')
          }
        }}
      >
        Continue
      </PrimaryButton>
    }
  </TravelRuleFieldsContainer >
}

export default TravelRuleFieldsWidget
